/**
 * Persistent league admin chrome: pillar sidebar (desktop) + sheet (mobile).
 */
import { type ComponentType, type ReactNode, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Menu } from 'lucide-react';
import { useLocation } from 'wouter';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { BreadcrumbBar } from '@/components/ui/breadcrumb-bar';
import { LeaguePillarNav } from '@/components/league/LeaguePillarNav';

type LeagueAdminShellProps = {
  children: ReactNode;
};

export function LeagueAdminShell({ children }: LeagueAdminShellProps) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [location] = useLocation();

  const { data: orgData } = useQuery<{ organizationId: string }>({
    queryKey: ['/api/league/forest-glade-org'],
    queryFn: async () => {
      const res = await fetch('/api/league/forest-glade-org', { credentials: 'include' });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.hint || err.error || 'Org not configured');
      }
      return res.json();
    },
    staleTime: 5 * 60_000,
  });

  const organizationId = orgData?.organizationId ?? null;
  const pathSegments = location.split('/').filter(Boolean);
  const lastSegment = pathSegments[pathSegments.length - 1];
  const sectionLabel = lastSegment
    ? lastSegment.replace(/[-_]/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
    : 'Overview';
  const breadcrumbItems = [{ label: 'League Admin' }, { label: sectionLabel, active: true }];

  return (
    <div className="min-h-dvh min-h-[100dvh] bg-[#020617] text-sport-text flex w-full">
      <aside
        className="hidden lg:flex w-[268px] shrink-0 flex-col border-r border-white/10 bg-[#050b18] sticky top-0 h-dvh overflow-y-auto"
        aria-label="League command pillars"
      >
        <LeaguePillarNav organizationId={organizationId} />
      </aside>

      <div className="flex-1 flex flex-col min-w-0 min-h-dvh min-h-[100dvh] pb-safe">
        <div className="lg:hidden sticky top-0 z-30 flex items-center gap-2 border-b border-white/10 bg-[#020617]/95 backdrop-blur-md px-3 py-2.5 pt-[max(0.625rem,env(safe-area-inset-top,0px))]">
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-11 min-h-[44px] min-w-[44px] border-white/20 text-white hover:bg-white/10 font-broadcast uppercase tracking-wider text-[10px] gap-2 touch-manipulation active:scale-[0.98]"
                aria-label="Open league command menu"
              >
                <Menu className="h-4 w-4 text-[#D4AF37]" />
                Pillars
              </Button>
            </SheetTrigger>
            <SheetContent
              side="left"
              className="w-[min(100vw-2rem,320px)] border-white/10 bg-[#050b18] p-0 overflow-y-auto"
            >
              <SheetHeader className="px-4 pt-4 pb-2 border-b border-white/10 text-left">
                <SheetTitle className="text-[#D4AF37] font-broadcast uppercase tracking-widest text-xs">
                  League command
                </SheetTitle>
              </SheetHeader>
              <LeaguePillarNav
                organizationId={organizationId}
                onNavigate={() => setMobileOpen(false)}
              />
            </SheetContent>
          </Sheet>
          <span className="text-[10px] font-broadcast uppercase tracking-[0.2em] text-white/50 truncate">
            Holistic command center
          </span>
        </div>

        <div className="hidden md:block">
          <BreadcrumbBar items={breadcrumbItems} />
        </div>
        <div className="flex-1 min-w-0">{children}</div>
      </div>
    </div>
  );
}

export function withLeagueAdminShell<P extends object>(Page: ComponentType<P>) {
  const Wrapped = (props: P) => (
    <LeagueAdminShell>
      <Page {...props} />
    </LeagueAdminShell>
  );
  Wrapped.displayName = `WithLeagueAdminShell(${Page.displayName || Page.name || 'Page'})`;
  return Wrapped;
}
